import { motion, AnimatePresence } from 'framer-motion';
import { Shield, CheckCircle, ArrowRight, ShoppingCart, Award, Clock, Star, ChevronLeft, ChevronRight, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useContactPopup } from '@/hooks/useContactPopup';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';

const ServiceForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+44',
    phone: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const submissions = JSON.parse(localStorage.getItem('contactSubmissions') || '[]');
    submissions.push({ ...formData, timestamp: new Date().toISOString(), source: 'amazon-registry-page' });
    localStorage.setItem('contactSubmissions', JSON.stringify(submissions));
    toast.success('Thank you! We\'ll contact you within 24 hours about Amazon Brand Registry enrollment.');
    setFormData({ name: '', email: '', countryCode: '+44', phone: '', message: '' });
  };

  const countryCodes = [
    { code: '+44', country: 'UK' },
    { code: '+1', country: 'US/Canada' },
    { code: '+91', country: 'India' },
    { code: '+61', country: 'Australia' },
    { code: '+353', country: 'Ireland' }
  ];

  return (
    <Card className="p-8 border-2 border-primary shadow-elegant-xl">
      <div className="mb-6">
        <Badge className="mb-3 bg-secondary text-secondary-foreground">Special Offer</Badge>
        <h3 className="text-2xl font-bold text-foreground mb-2">Amazon Brand Registry - Only £499</h3>
        <p className="text-muted-foreground">Complete trademark registration and Amazon Brand Registry enrollment. Fill out the form and we'll contact you within 24 hours.</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="service-name">Name *</Label>
          <Input
            id="service-name"
            placeholder="John Smith"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="service-email">Email *</Label>
          <Input
            id="service-email"
            type="email"
            placeholder="john@example.com"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="service-phone">Contact Number *</Label>
          <div className="flex gap-2">
            <Select value={formData.countryCode} onValueChange={(value) => setFormData({ ...formData, countryCode: value })}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {countryCodes.map((item) => (
                  <SelectItem key={item.code} value={item.code}>
                    {item.code} ({item.country})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              id="service-phone"
              type="tel"
              placeholder="1234567890"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              required
              className="flex-1"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="service-message">Brand & Product Details</Label>
          <Textarea
            id="service-message"
            placeholder="Tell us about your Amazon brand and products (brand name, product categories, current Amazon selling experience)"
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            rows={4}
          />
        </div>

        <Button type="submit" className="w-full bg-gradient-to-r from-primary to-secondary hover:shadow-glow transition-all duration-300">
          Enroll in Amazon Brand Registry
          <ArrowRight className="ml-2 w-4 h-4" />
        </Button>
      </form>
    </Card>
  );
};

const TestimonialCard = ({ testimonial }) => (
  <Card className="p-6 h-full flex flex-col border-border shadow-lg hover:shadow-xl transition-shadow duration-300">
    <div className="flex gap-1 mb-4">
      {[1, 2, 3, 4, 5].map((star) => {
        const rating = testimonial.rating;
        const isFullStar = star <= rating;
        const isHalfStar = star === Math.ceil(rating) && rating % 1 !== 0;
        
        return (
          <div key={star} className="relative w-5 h-5">
            {isHalfStar ? (
              <>
                <Star className="w-5 h-5 text-gray-300 absolute inset-0" fill="currentColor" />
                <div className="overflow-hidden absolute w-2.5 h-5">
                  <Star className="w-5 h-5 text-yellow-400" fill="currentColor" />
                </div>
              </>
            ) : (
              <Star 
                className={`w-5 h-5 ${isFullStar ? 'text-yellow-400' : 'text-gray-300'}`} 
                fill="currentColor" 
              />
            )}
          </div>
        );
      })}
    </div>
    
    <p className="text-base text-foreground mb-6 leading-relaxed italic flex-1">
      &quot;{testimonial.content}&quot;
    </p>
    
    <div className="border-t border-border pt-4">
      <p className="font-semibold text-foreground">{testimonial.name}</p>
      <p className="text-sm text-muted-foreground mt-1">{testimonial.role}</p>
    </div>
  </Card>
);

export const AmazonBrandRegistryPage = () => {
  const { openPopup } = useContactPopup();
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const testimonials = [
    {
      name: 'Emma Thompson',
      role: 'E-commerce Manager, Heritage Home Goods',
      content: 'Getting into Amazon Brand Registry seemed impossible until we found Trademark Assist. They guided us through trademark registration specifically for Amazon\'s requirements, then handled the Brand Registry enrollment process. Our sales increased 40% in three months thanks to the added trust and protection.',
      rating: 5
    },
    {
      name: 'Oliver Martinez',
      role: 'Founder, Urban Wellness Products',
      content: 'The counterfeit problem on Amazon was killing our business. Trademark Assist got us into Brand Registry fast, and we immediately started removing fake listings. Our brand reputation recovered, and customer complaints dropped by 90%. Worth every penny.',
      rating: 5
    },
    {
      name: 'Sarah Mitchell',
      role: 'CEO, EcoLiving Essentials',
      content: 'We were losing thousands to counterfeiters hijacking our listings. Trademark Assist registered our trademark and enrolled us in Brand Registry within 6 months. The enhanced brand protection tools gave us complete control over our Amazon presence. Game changer.',
      rating: 5
    },
    {
      name: 'David Williams',
      role: 'Director, Premium Pet Supplies UK',
      content: 'The A+ Content and Sponsored Brands access we got through Brand Registry transformed our conversion rates. Trademark Assist handled everything professionally, and now we have the tools to compete with major brands on Amazon. Our revenue doubled in 6 months.',
      rating: 4.5
    },
    {
      name: 'Priya Sharma',
      role: 'Founder, Artisan Beauty Co',
      content: 'I thought Brand Registry was only for big companies, but Trademark Assist showed us how small brands like ours could benefit. The protection from copycats and access to enhanced marketing tools leveled the playing field. Essential for any serious Amazon seller.',
      rating: 5
    }
  ];

  const getVisibleTestimonials = () => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const testimonialsPerView = isMobile ? 1 : 3;
    const visible = [];
    for (let i = 0; i < testimonialsPerView; i++) {
      const index = (currentTestimonial + i) % testimonials.length;
      visible.push({ ...testimonials[index], originalIndex: index });
    }
    return visible;
  };

  const nextTestimonial = () => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const step = isMobile ? 1 : 3;
    setCurrentTestimonial((prev) => (prev + step) % testimonials.length);
  };

  const prevTestimonial = () => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const step = isMobile ? 1 : 3;
    setCurrentTestimonial((prev) => (prev - step + testimonials.length) % testimonials.length);
  };

  useEffect(() => {
    const testimonialInterval = setInterval(() => {
      nextTestimonial();
    }, 6000);
    return () => clearInterval(testimonialInterval);
  }, [currentTestimonial]);

  const steps = [
    {
      number: '01',
      title: 'Trademark Registration',
      description: 'First, we register your trademark with the UK IPO (or verify your existing registration meets Amazon\'s requirements). This registered trademark is mandatory for Brand Registry enrollment. We handle the entire trademark process from search to approval.'
    },
    {
      number: '02',
      title: 'Brand Registry Application',
      description: 'Once your trademark is registered (or close to approval), we prepare and submit your Amazon Brand Registry application. We provide all required documentation, including trademark details, brand website, product images, and packaging examples.'
    },
    {
      number: '03',
      title: 'Enrollment & Training',
      description: 'After Amazon approves your enrollment (typically 1-2 weeks), we guide you through using Brand Registry tools—protecting your listings, reporting infringements, accessing enhanced content features, and leveraging advertising options available only to registered brands.'
    }
  ];

  const usps = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Complete Counterfeit Protection',
      description: 'Brand Registry gives you powerful tools to remove counterfeit products and unauthorized sellers from your listings. You gain access to Amazon\'s automated brand protection systems that monitor for violations, plus the ability to report infringements directly. Our clients typically see counterfeit listings drop by 95% within the first month of enrollment.'
    },
    {
      icon: <Package className="w-8 h-8" />,
      title: 'Enhanced Marketing Tools',
      description: 'Unlock A+ Content (enhanced product descriptions with rich media), Sponsored Brands advertising (headline ads featuring your logo), and Amazon Stores (custom multi-page shopping experience). These tools dramatically improve conversion rates—our clients typically see 20-40% increases in sales after implementing enhanced content features.'
    },
    {
      icon: <ShoppingCart className="w-8 h-8" />,
      title: 'Complete Listing Control',
      description: 'Take full control of your product detail pages. Brand Registry gives you the authority to update product titles, images, and descriptions even when other sellers are on the listing. You decide how your brand is presented on Amazon, maintaining consistency and quality across all your products.'
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: 'End-to-End Service',
      description: 'We don\'t just enroll you in Brand Registry—we handle the trademark registration first (if needed), guide you through Amazon\'s verification process, and provide training on using all Brand Registry features effectively. You get complete support from initial trademark search through to leveraging advanced Amazon marketing tools.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 bg-gradient-to-br from-primary/5 via-background to-secondary/5">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <Badge className="mb-4 bg-primary text-primary-foreground">Amazon Brand Registry Service</Badge>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6">
                Amazon Brand Registry Enrollment and Protection
              </h1>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                Protect your Amazon business from counterfeiters and unlock powerful marketing tools with Amazon Brand Registry. Our comprehensive service handles trademark registration (the prerequisite for Brand Registry) and guides you through Amazon's enrollment process. Once enrolled, you gain access to enhanced brand content, sponsored advertising, and proactive counterfeit removal tools. Stop losing sales to fake products and unauthorized sellers—with Brand Registry, you control your brand's Amazon presence. At £499, we make professional Brand Registry enrollment accessible while handling all the complex requirements.
              </p>
              
              {/* CTA Button */}
              <div className="flex gap-4">
                <Button 
                  onClick={openPopup}
                  size="lg"
                  className="bg-primary hover:bg-primary/90 text-primary-foreground group"
                >
                  Enroll in Brand Registry
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
              <ServiceForm />
            </motion.div>
          </div>
        </div>
      </section>

      {/* About Service */}
      <section className="py-20 px-4 bg-background">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <img src="https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&h=600&fit=crop" alt="Amazon Brand Registry" className="rounded-lg shadow-elegant-xl w-full h-auto" />
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">What Is Amazon Brand Registry?</h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                Amazon Brand Registry is Amazon's program for protecting registered trademarks and giving brand owners enhanced control over their product listings. To enroll, you must have a registered trademark (pending applications are sometimes accepted). Once enrolled, you gain powerful tools: proactive brand protection that monitors for potential infringements, the ability to report and remove counterfeit products, control over your product detail pages even when multiple sellers are present, access to A+ Content (rich product descriptions with images and comparison charts), Sponsored Brands advertising featuring your logo, and Amazon Stores (custom multi-page brand experience). For serious Amazon sellers, Brand Registry is essential—it's the difference between constantly fighting copycats and having the tools to protect and grow your brand effectively.
              </p>
              
              {/* CTA Button */}
              <div className="mt-8">
                <Button 
                  onClick={openPopup}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground group"
                  size="lg"
                >
                  Enroll in Brand Registry
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-20 px-4 bg-gradient-to-br from-secondary/5 to-background">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">How We Work: Amazon Brand Registry Process</h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                Getting into Amazon Brand Registry requires a registered trademark first. Our three-step process handles both trademark registration and Brand Registry enrollment, ensuring you meet all requirements and maximize the benefits of enrollment.
              </p>
            </motion.div>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}>
                <Card className="p-8 h-full border-2 border-transparent hover:border-primary transition-all duration-300 relative overflow-hidden group">
                  <div className="absolute top-0 right-0 text-8xl font-bold text-primary/5 group-hover:text-primary/10 transition-colors duration-300">{step.number}</div>
                  <div className="relative z-10">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center text-primary-foreground font-bold text-2xl mb-6 shadow-lg">{step.number}</div>
                    <h3 className="text-xl font-semibold text-foreground mb-4">{step.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
          
          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Button 
              onClick={openPopup}
              className="bg-secondary hover:bg-secondary/90 text-secondary-foreground group"
              size="lg"
            >
              Get Started Now
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 bg-background">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <Badge className="mb-4 bg-secondary/10 text-secondary border-secondary/20">Simple Pricing</Badge>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">Amazon Brand Registry Service</h2>
              <p className="text-lg text-muted-foreground">Everything you need to protect your Amazon business</p>
            </motion.div>
          </div>
          
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Card className="p-10 border-2 border-primary hover:shadow-elegant-2xl transition-all duration-300 relative overflow-hidden bg-gradient-to-br from-primary/5 to-background">
              <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-full -mr-32 -mt-32"></div>
              
              <div className="relative z-10 text-center">
                <h3 className="text-3xl font-bold text-foreground mb-3">Amazon Brand Registry</h3>
                <div className="mb-6">
                  <div className="text-5xl font-bold text-primary mb-2">from £120</div>
                  <Badge className="bg-green-500 text-white text-sm">100% Satisfaction Guaranteed</Badge>
                </div>
                
                <div className="space-y-4 mb-8 text-left max-w-2xl mx-auto">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-success mt-1 flex-shrink-0" />
                    <span className="text-base text-foreground">
                      <strong>Comprehensive review</strong> of your Amazon case to ensure full compliance with UK regulations.
                    </span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-success mt-1 flex-shrink-0" />
                    <span className="text-base text-foreground">
                      <strong>Expert Preparation</strong> of all required documents to build a strong case for your Amazon service request.
                    </span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-success mt-1 flex-shrink-0" />
                    <span className="text-base text-foreground">
                      <strong>Complete filing process</strong> with Amazon ensuring accuracy, timely submission and ongoing monitoring.
                    </span>
                  </div>
                </div>
                
                <Button 
                  onClick={openPopup} 
                  size="lg"
                  className="w-full max-w-md bg-gradient-to-r from-primary to-secondary hover:shadow-glow transition-all duration-300 text-lg py-6"
                >
                  Get Started Now
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 bg-gradient-to-br from-primary/5 via-background to-secondary/5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">Why Choose Trademark Assist for Amazon Brand Registry</h2>
              <p className="text-lg text-muted-foreground max-w-4xl mx-auto leading-relaxed">
                We've helped hundreds of Amazon sellers enroll in Brand Registry by handling both trademark registration and Amazon enrollment—plus training you to maximize its powerful protection and marketing tools.
              </p>
            </motion.div>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {usps.map((usp, index) => (
              <motion.div key={index} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }}>
                <Card className="p-8 border-2 border-border hover:border-primary/50 transition-all duration-500 h-full group relative overflow-hidden bg-white hover:bg-gradient-to-br hover:from-primary/5 hover:to-secondary/5 hover:shadow-[0_20px_60px_-15px_rgba(1,33,105,0.3)] hover:scale-[1.02]">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-in-out"></div>
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary/20 to-transparent rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 -mr-16 -mt-16"></div>
                  <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-secondary/20 to-transparent rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 -ml-16 -mb-16"></div>
                  <div className="relative z-10">
                    <div className="relative w-20 h-20 mb-6 group-hover:mb-8 transition-all duration-500">
                      <div className="absolute inset-0 bg-gradient-to-br from-primary to-secondary rounded-2xl opacity-0 group-hover:opacity-20 blur-xl transition-all duration-500"></div>
                      <div className="relative w-20 h-20 bg-gradient-to-br from-primary/10 to-secondary/10 group-hover:from-primary group-hover:to-secondary rounded-2xl flex items-center justify-center text-primary group-hover:text-white transition-all duration-500 group-hover:rotate-[360deg] group-hover:scale-110 shadow-lg group-hover:shadow-2xl">
                        {usp.icon}
                      </div>
                    </div>
                    <h3 className="text-2xl font-semibold text-foreground mb-4 group-hover:text-primary transition-all duration-500 group-hover:tracking-wide">{usp.title}</h3>
                    <p className="text-muted-foreground leading-relaxed group-hover:text-foreground/90 transition-all duration-500">{usp.description}</p>
                    <div className="mt-6 h-1 w-0 group-hover:w-full bg-gradient-to-r from-primary to-secondary rounded-full transition-all duration-700 ease-out"></div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1556742400-b5b7c882f267?w=1920&h=600&fit=crop" alt="Amazon Selling" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/95 via-primary-dark/90 to-secondary/85"></div>
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-primary-foreground">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Protect Your Amazon Business - Brand Registry from £120</h2>
            <p className="text-lg mb-8 text-primary-foreground/90 leading-relaxed max-w-3xl mx-auto">
              Stop losing sales to counterfeiters and unauthorized sellers. Amazon Brand Registry gives you the tools to protect your listings, remove fake products, and access enhanced marketing features that drive conversions. We handle trademark registration and Brand Registry enrollment, ensuring you meet all requirements and leverage every benefit. Join thousands of protected Amazon brands.
            </p>
            <Button onClick={openPopup} size="lg" variant="secondary" className="text-lg px-8 bg-primary-foreground text-primary hover:bg-primary-foreground/90 hover:scale-105 transition-all duration-300 shadow-elegant-xl">
              Enroll in Brand Registry Now
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 bg-gradient-to-br from-secondary/5 to-background">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16"><h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">What Amazon Sellers Say About Our Brand Registry Service</h2></div>
          <div className="relative">
            <button onClick={prevTestimonial} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 z-10 bg-primary text-primary-foreground rounded-full p-3 shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300" aria-label="Previous testimonials"><ChevronLeft className="w-6 h-6" /></button>
            <button onClick={nextTestimonial} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 z-10 bg-primary text-primary-foreground rounded-full p-3 shadow-lg hover:shadow-xl hover:scale-110 transition-all duration-300" aria-label="Next testimonials"><ChevronRight className="w-6 h-6" /></button>
            <div className="overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.div key={currentTestimonial} initial={{ opacity: 0, x: 100 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -100 }} transition={{ duration: 0.5, ease: "easeInOut" }} className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {getVisibleTestimonials().map((testimonial, index) => (<div key={`${testimonial.originalIndex}-${index}`}><TestimonialCard testimonial={testimonial} /></div>))}
                </motion.div>
              </AnimatePresence>
            </div>
            <div className="flex justify-center gap-2 mt-8">
              {Array.from({ length: Math.ceil(testimonials.length / 3) }).map((_, index) => (<button key={index} onClick={() => setCurrentTestimonial(index * 3)} className={`h-2 rounded-full transition-all duration-500 ease-out ${Math.floor(currentTestimonial / 3) === index ? 'bg-primary w-8 shadow-glow' : 'bg-border w-2 hover:bg-primary/50 hover:w-4'}`} aria-label={`View testimonial group ${index + 1}`} />))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 px-4 bg-background">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16"><h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">Frequently Asked Questions About Amazon Brand Registry</h2></div>
          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="border border-border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:text-primary transition-colors">Why do I need Brand Registry to sell on Amazon?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">You don't technically need Brand Registry to sell on Amazon, but without it, you're vulnerable to counterfeiters, hijackers, and unauthorized sellers who can damage your brand reputation and steal your sales. Brand Registry gives you powerful tools to remove fake listings, control your product detail pages, and report infringements. Plus, you unlock enhanced marketing features like A+ Content and Sponsored Brands that significantly improve conversion rates. For serious Amazon sellers, Brand Registry is essential protection and a competitive advantage.</AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2" className="border border-border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:text-primary transition-colors">What's the difference between having a trademark and being in Brand Registry?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">A trademark gives you legal protection outside Amazon—you can take legal action against anyone using your brand without permission. Brand Registry is Amazon's internal system for managing that trademark on their platform. It gives you access to Amazon-specific tools: automated brand protection monitoring, the ability to report and remove infringements directly through Amazon, control over your product listings, and enhanced content and advertising features. You need a registered trademark to enroll in Brand Registry, but having a trademark alone doesn't give you these Amazon-specific benefits.</AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3" className="border border-border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:text-primary transition-colors">How long does it take to get into Brand Registry?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">The timeline depends on whether you already have a registered trademark. If you need trademark registration first, that typically takes 4-6 months in the UK. Once your trademark is registered (or close to registration), the Brand Registry application process takes 1-2 weeks. Amazon sometimes accepts pending trademark applications if you can demonstrate active use of the mark. We'll assess your situation and provide a realistic timeline. Our Professional and Enterprise packages can expedite the trademark process where possible.</AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-4" className="border border-border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:text-primary transition-colors">Can I enroll in Brand Registry with a logo trademark or does it have to be text?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">Amazon accepts both word marks (text-based trademarks) and design marks (logos), though word marks are generally preferred and easier to enforce. If you have a logo trademark, ensure the brand name appears prominently within the logo design, as Amazon needs to verify the mark matches your product listings. We can advise on whether your existing trademark (if you have one) will work for Brand Registry or if you should register a word mark version. Many brands register both for maximum protection.</AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-5" className="border border-border rounded-lg px-6">
              <AccordionTrigger className="text-left hover:text-primary transition-colors">What happens to other sellers on my listings after I enroll in Brand Registry?</AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed">Brand Registry doesn't automatically remove all other sellers from your listings. However, it gives you tools to manage who can sell your products. If you're the manufacturer or brand owner, you can report unauthorized sellers and Amazon will investigate. You gain the ability to update listing content (which other sellers can't do), making it clear you control the brand. For products where you're the only legitimate seller, you can use Brand Registry to report violations and have unauthorized sellers removed. If you have authorized resellers, they can continue selling, but you control the product detail page content. We provide guidance on managing seller permissions effectively.</AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>
    </div>
  );
};

export default AmazonBrandRegistryPage;